import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Search } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleER } from "@/components/er/ModuleTitleER";
import {
  CATEGORY_LABELS,
  PRIORITY_LABELS,
  STATUS_LABELS,
  priorityPillClass,
  statusPillClass,
} from "./erLabels";

export default function ErCaseQueue() {
  const [cases, setCases] = useState([]);
  const [slaOverdue, setSlaOverdue] = useState(0);
  const [filters, setFilters] = useState({
    status: "",
    category: "",
    priority: "",
    campus_id: "",
    q: "",
    include_archived: false,
  });
  const [campuses, setCampuses] = useState([]);

  const load = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== false) params[k] = v;
      });
      const { data } = await api.get("/er/cases", { params });
      setCases(data.cases || []);
      setSlaOverdue(data.sla_overdue_count || 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/campuses").then((r) => setCampuses(r.data)).catch(() => setCampuses([]));
  }, []);

  useEffect(() => {
    load();
     
  }, [filters]);

  return (
    <div className="space-y-6" data-testid="er-case-queue-page">
      <div>
        <ModuleTitleER />
        <h1 className="mt-3 font-serif text-forest-900 text-4xl">Case Queue</h1>
        <p className="mt-2 text-charcoal-soft">
          Every open concern brought to People Operations. Handle each with warmth, thoroughness,
          and care.
        </p>
      </div>

      {slaOverdue > 0 && (
        <div
          className="patria-card p-4 border-l-4 border-terracotta-500 flex items-start gap-3 bg-terracotta-500/5"
          data-testid="er-sla-banner"
        >
          <AlertTriangle className="h-5 w-5 text-terracotta-500 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="font-serif text-forest-900 text-lg">
              {slaOverdue} case{slaOverdue === 1 ? "" : "s"} overdue for acknowledgment
            </p>
            <p className="text-sm text-charcoal-soft mt-1">
              Cases must be acknowledged within 2 business days. Please review the flagged rows
              below.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <section className="patria-card p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative sm:col-span-2 lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-muted" strokeWidth={1.5} />
            <input
              className="patria-input pl-9 py-2 text-sm w-full"
              placeholder="Search case # or description"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="er-queue-search"
            />
          </div>
          <select
            className="patria-input py-2 text-sm"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            data-testid="er-queue-status"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
          <select
            className="patria-input py-2 text-sm"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            data-testid="er-queue-category"
          >
            <option value="">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
          <select
            className="patria-input py-2 text-sm"
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            data-testid="er-queue-priority"
          >
            <option value="">All priorities</option>
            {Object.entries(PRIORITY_LABELS).map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
          <select
            className="patria-input py-2 text-sm"
            value={filters.campus_id}
            onChange={(e) => setFilters({ ...filters, campus_id: e.target.value })}
            data-testid="er-queue-campus"
          >
            <option value="">All campuses</option>
            {campuses.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-charcoal-soft">
            <input
              type="checkbox"
              checked={filters.include_archived}
              onChange={(e) => setFilters({ ...filters, include_archived: e.target.checked })}
              data-testid="er-queue-archived"
            />
            Include archived
          </label>
        </div>
      </section>

      {/* Table */}
      <section className="patria-card overflow-hidden" data-testid="er-cases-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-forest-600 text-cream-100">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Case #</th>
                <th className="text-left px-4 py-3 font-medium">Reporter</th>
                <th className="text-left px-4 py-3 font-medium">Category</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Priority</th>
                <th className="text-left px-4 py-3 font-medium">Submitted</th>
                <th className="text-left px-4 py-3 font-medium">SLA</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-sage-300/60">
              {cases.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-charcoal-muted italic">
                    No cases match the current filters.
                  </td>
                </tr>
              )}
              {cases.map((c) => (
                <tr key={c.id} data-testid={`er-case-row-${c.case_number}`}>
                  <td className="px-4 py-3 font-mono text-forest-900">{c.case_number}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ${
                        c.reporter_type === "anonymous"
                          ? "bg-terracotta-500/15 text-terracotta-600 border border-terracotta-500/30"
                          : "bg-sage-300/40 text-forest-800 border border-sage-300"
                      }`}
                    >
                      {c.reporter_type === "anonymous" ? "Anonymous" : "Identified"}
                    </span>
                  </td>
                  <td className="px-4 py-3">{CATEGORY_LABELS[c.category] || c.category}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${statusPillClass(c.status)}`}>
                      {STATUS_LABELS[c.status] || c.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${priorityPillClass(c.priority)}`}>
                      {PRIORITY_LABELS[c.priority] || c.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-charcoal-soft">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {c.sla_overdue ? (
                      <span
                        className="inline-flex items-center rounded-full bg-terracotta-500 text-cream-100 px-2 py-0.5 text-[11px] font-medium"
                        data-testid={`er-case-sla-overdue-${c.case_number}`}
                      >
                        Overdue
                      </span>
                    ) : (
                      <span className="text-charcoal-muted text-xs">On track</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/er/cases/${c.id}`}
                      className="patria-link text-sm"
                      data-testid={`er-case-open-${c.case_number}`}
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
